import { z } from "zod";

const headerMapSchema = z.record(z.string(), z.string());

export const capturedBodySchema = z.object({
  text: z.string(),
  truncated: z.boolean(),
});

export const consoleEntrySchema = z.object({
  timestamp: z.number(),
  level: z.enum(["debug", "log", "info", "warn", "error", "trace", "assert", "table", "dir"]),
  args: z.array(z.unknown()),
  stack: z.string().optional(),
  truncated: z.boolean().optional(),
});

export const networkEntrySchema = z.object({
  id: z.string(),
  timestamp: z.number(),
  endedAt: z.number(),
  duration: z.number(),
  method: z.string(),
  url: z.string(),
  status: z.number().optional(),
  requestHeaders: headerMapSchema,
  responseHeaders: headerMapSchema,
  requestBody: capturedBodySchema.optional(),
  responseBody: capturedBodySchema.optional(),
  requestId: z.string().optional(),
  traceparent: z.string().optional(),
  failed: z.boolean(),
  error: z.string().optional(),
});

const actionBaseSchema = z.object({ timestamp: z.number() });

export const actionEntrySchema = z.discriminatedUnion("type", [
  actionBaseSchema.extend({
    type: z.literal("click"),
    target: z.string(),
    text: z.string(),
    masked: z.boolean(),
  }),
  actionBaseSchema.extend({
    type: z.literal("input"),
    target: z.string(),
    value: z.string().optional(),
    masked: z.boolean(),
  }),
  actionBaseSchema.extend({
    type: z.literal("navigation"),
    url: z.string(),
    navigationType: z.enum(["pushState", "replaceState", "popstate", "hashchange"]),
  }),
  actionBaseSchema.extend({
    type: z.literal("scroll"),
    target: z.string(),
    x: z.number(),
    y: z.number(),
  }),
]);

export const deviceInfoSchema = z.object({
  pageUrl: z.string(),
  userAgent: z.string(),
  browser: z.string(),
  os: z.string(),
  viewport: z.object({ width: z.number(), height: z.number() }),
  devicePixelRatio: z.number(),
  zoom: z.number(),
  locale: z.string(),
  timezone: z.string(),
  connection: z
    .object({
      effectiveType: z.string().optional(),
      downlink: z.number().optional(),
      rtt: z.number().optional(),
      saveData: z.boolean().optional(),
    })
    .optional(),
});

export const captureSnapshotSchema = z.object({
  replayEvents: z.array(z.unknown()),
  console: z.array(consoleEntrySchema),
  network: z.array(networkEntrySchema),
  actions: z.array(actionEntrySchema),
  device: deviceInfoSchema,
  context: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  startedAt: z.number(),
  endedAt: z.number(),
  truncatedReplay: z.boolean().optional(),
  /** FR-CAP-07: exceptions inside capture hooks that were swallowed while recording. */
  captureErrors: z.number().int().nonnegative().optional(),
});

export type CapturedBody = z.infer<typeof capturedBodySchema>;
export type ConsoleEntry = z.infer<typeof consoleEntrySchema>;
export type NetworkEntry = z.infer<typeof networkEntrySchema>;
export type ActionEntry = z.infer<typeof actionEntrySchema>;
export type DeviceInfo = z.infer<typeof deviceInfoSchema>;
export type CaptureSnapshot = z.infer<typeof captureSnapshotSchema>;
